import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import ProductCard from "../components/ProductCard";
import QuoteModal from "../components/QuoteModal";
import { products, productCategories } from "../data/mock";

export default function Products() {
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [open, setOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = category === "All" || p.category === category;
      const q = query.trim().toLowerCase();
      const matchQ = !q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [category, query]);

  const inquire = (p) => { setActiveProduct(p); setOpen(true); };

  return (
    <main>
      <section className="brand-navy text-white py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold">Catalog</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-2">Our Products</h1>
          <nav className="text-sm text-white/60 mt-3">
            <Link to="/" className="hover:text-white">Home</Link> / <span className="text-white">Products</span>
          </nav>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="bg-white rounded-md border border-gray-100 p-5 sticky top-24">
              <h3 className="text-base font-bold text-brand-navy mb-3">Categories</h3>
              <ul className="space-y-1">
                {productCategories.map((c) => (
                  <li key={c}>
                    <button
                      onClick={() => setCategory(c)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                        category === c ? "bg-brand-navy text-white" : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {c}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <label className="text-xs font-semibold text-brand-navy block mb-2">Search</label>
                <div className="relative">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-sm outline-none focus:border-brand-blue"
                  />
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-600">{filtered.length} product{filtered.length !== 1 ? "s" : ""} found</p>
              {category !== "All" && <span className="text-sm text-brand-navy font-semibold">Category: {category}</span>}
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-md border border-gray-100">
                <p className="text-gray-500">No products found. Try a different search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {filtered.map((p) => (
                  <ProductCard key={p.id} product={p} onInquire={inquire} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <QuoteModal open={open} onOpenChange={setOpen} productName={activeProduct?.name} />
    </main>
  );
}
